var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CoerceoRules_1;
import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { BooleanConfig } from '../../config/BooleanConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { GameNode } from '../../jscaip/AI/GameNode';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { GameStatus } from '../../jscaip/GameStatus';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { Debug } from '../../utils/Debug';
import { CoerceoFailure } from './CoerceoFailure';
import { CoerceoMove } from './CoerceoMove';
import { CoerceoState } from './CoerceoState';
export class CoerceoNode extends GameNode {
}
let CoerceoRules = class CoerceoRules extends ConfigurableRules {
    static { CoerceoRules_1 = this; }
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Coerceo`,
        config: {
            smallBoard: new BooleanConfig(false, () => $localize `Use small board`),
        },
    });
    static get() {
        if (CoerceoRules_1.singleton.isAbsent()) {
            CoerceoRules_1.singleton = MGPOptional.of(new CoerceoRules_1());
        }
        return CoerceoRules_1.singleton.get();
    }
    getInitialState(config) {
        const _ = FourStatePiece.EMPTY;
        const N = FourStatePiece.UNREACHABLE;
        const O = FourStatePiece.ZERO;
        const X = FourStatePiece.ONE;
        let board;
        if (config.smallBoard) {
            board = [
                [N, N, N, N, N, N, N, N, N],
                [N, N, N, O, _, O, N, N, N],
                [_, _, O, _, _, _, O, _, _],
                [_, _, _, O, _, O, _, _, _],
                [_, _, _, X, _, X, _, _, _],
                [_, _, X, _, _, _, X, _, _],
                [N, N, N, X, _, X, N, N, N],
                [N, N, N, N, N, N, N, N, N],
            ];
        }
        else {
            board = [
                [N, N, N, N, N, N, O, _, O, N, N, N, N, N, N],
                [N, N, N, _, _, O, _, _, _, O, _, _, N, N, N],
                [_, X, _, X, _, _, O, _, O, _, _, X, _, X, _],
                [X, _, _, _, X, _, _, _, _, _, X, _, _, _, X],
                [_, X, _, X, _, _, _, _, _, _, _, X, _, X, _],
                [_, O, _, O, _, _, _, _, _, _, _, O, _, O, _],
                [O, _, _, _, O, _, _, _, _, _, O, _, _, _, O],
                [_, O, _, O, _, _, X, _, X, _, _, O, _, O, _],
                [N, N, N, _, _, X, _, _, _, X, _, _, N, N, N],
                [N, N, N, N, N, N, X, _, X, N, N, N, N, N, N],
            ];
        }
        return new CoerceoState(board, 0, PlayerNumberMap.of(0, 0), PlayerNumberMap.of(0, 0));
    }
    getRulesConfigDescription() {
        return CoerceoRules_1.RULES_CONFIG_DESCRIPTION;
    }
    applyLegalMove(move, state, _config, _info) {
        if (CoerceoMove.isTileExchange(move)) {
            return this.applyLegalTileExchange(move, state);
        }
        else {
            return this.applyLegalMovement(move, state);
        }
    }
    applyLegalTileExchange(move, state) {
        const newBoard = state.getCopiedBoard();
        const captured = move.coord;
        newBoard[captured.y][captured.x] = FourStatePiece.EMPTY;
        const currentPlayer = state.getCurrentPlayer();
        const newCaptures = state.captures.getCopy();
        newCaptures.add(currentPlayer, 1);
        const newTiles = state.tiles.getCopy();
        newTiles.add(currentPlayer, -2);
        const afterCapture = new CoerceoState(newBoard, state.turn, newTiles, newCaptures);
        const afterTileRemoval = afterCapture.removeTilesIfNeeded(captured, false);
        const resultingState = new CoerceoState(afterTileRemoval.getCopiedBoard(), afterTileRemoval.turn + 1, afterTileRemoval.tiles, afterTileRemoval.captures);
        return resultingState;
    }
    applyLegalMovement(move, state) {
        // Move the piece
        const afterMovement = state.applyLegalMovement(move);
        // removes emptied tiles
        const afterTilesRemoved = afterMovement.removeTilesIfNeeded(move.getStart(), true);
        // removes captured pieces
        const afterCaptures = afterTilesRemoved.doMovementCaptures(move);
        const resultingState = new CoerceoState(afterCaptures.board, state.turn + 1, afterCaptures.tiles, afterCaptures.captures);
        return resultingState;
    }
    isLegal(move, state) {
        if (CoerceoMove.isTileExchange(move)) {
            return this.isLegalTileExchange(move, state);
        }
        else {
            return this.isLegalMovement(move, state);
        }
    }
    isLegalTileExchange(move, state) {
        if (state.tiles.get(state.getCurrentPlayer()) < 2) {
            return MGPValidation.failure(CoerceoFailure.NOT_ENOUGH_TILES_TO_EXCHANGE());
        }
        const captured = state.getPieceAt(move.coord);
        if (captured === FourStatePiece.UNREACHABLE ||
            captured === FourStatePiece.EMPTY) {
            return MGPValidation.failure(CoerceoFailure.CANNOT_CAPTURE_FROM_EMPTY());
        }
        if (captured.is(state.getCurrentPlayer())) {
            return MGPValidation.failure(RulesFailure.CANNOT_SELF_CAPTURE());
        }
        return MGPValidation.SUCCESS;
    }
    isLegalMovement(move, state) {
        Utils.assert(state.getPieceAt(move.getStart()) !== FourStatePiece.UNREACHABLE, 'Cannot start with a coord outside the board ' + move.getStart().toString() + '.');
        Utils.assert(state.getPieceAt(move.getEnd()) !== FourStatePiece.UNREACHABLE, 'Cannot end with a coord outside the board ' + move.getEnd().toString() + '.');
        const starter = state.getPieceAt(move.getStart());
        if (starter === FourStatePiece.EMPTY) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (starter.is(state.getCurrentOpponent())) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        const lander = state.getPieceAt(move.getEnd());
        if (lander.is(state.getCurrentPlayer())) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        return MGPValidation.SUCCESS;
    }
    getGameStatus(node) {
        const state = node.gameState;
        const pieceMap = state.toPieceMap();
        if (pieceMap.get(FourStatePiece.ONE).isAbsent()) {
            return GameStatus.ZERO_WON;
        }
        if (pieceMap.get(FourStatePiece.ZERO).isAbsent()) {
            return GameStatus.ONE_WON;
        }
        return GameStatus.ONGOING;
    }
};
CoerceoRules = CoerceoRules_1 = __decorate([
    Debug.log
], CoerceoRules);
export { CoerceoRules };
